import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import classnames from 'classnames'
import Badge from '../../../../components/Badge'
import CopyableValue from '../../../../components/CopyableValue'
import { formatCurrency } from '../../../../util/string'
import { scheduleScrollAfterLayoutSettle } from '../../../../util/scroll'
import { BillingOptionsResult, BillingOptionObject } from '../../../../apis/types'
import {
  AccountStatusMap,
  BillingStatusMap,
  ServiceAccountWithInvoices,
  STATUS_CHIP_POSSIBLE_VALUES,
  StatusChipKey,
  getArBillingStanding,
  getArBillingStandingBadgeLabel,
  getBillingModeChip,
  getHeaderBalanceAmounts,
  getKeAccountStatus,
  getOptiRateStatus,
  getPendingCloseTooltip,
  getUtilityAccountStatus,
  getVnemStatus,
  HEADER_BALANCE_CHIP_TOOLTIPS,
} from './accountStatusUtils'
import { HEADER_CHIP_SLOT_CLASS, STATUS_GRID_CLASS, isHeaderAmountChip } from './tenantAccountStatusColumns'
import { DEFAULT_HEADER_CHIP_IDS, HEADER_CHIP_LABELS, type HeaderChipId } from './headerChipPrefs'
import HeaderChipsPicker from './HeaderChipsPicker'
import PortaledTooltip from './PortaledTooltip'
import StatusChipTooltipContent from './StatusChipTooltipContent'
import { AccountTabNavSlotsContext, countFittingTabItems } from './TabPanel'

export interface TenantAccountPanelProps {
  account: ServiceAccountWithInvoices
  billingStatuses: BillingStatusMap
  accountStatuses: AccountStatusMap
  billingOptions?: BillingOptionsResult
  /** Controlled open state from parent (preferred for deep-link / expand-all). */
  open?: boolean
  defaultOpen?: boolean
  /** Force open (e.g. deep-link target) — opens when true even if previously collapsed. */
  forceOpen?: boolean
  expandAllOpen?: boolean
  expandAllVersion?: number
  onOpenChange?: (open: boolean) => void
  isHighlighted?: boolean
  /** Distance from scrollport top to pin the header (below page header). */
  stickyOffset?: number
  stickyZIndex?: number
  /** When true (default), expanding the panel scrolls it into view. */
  autoScrollOnExpand?: boolean
  children?: React.ReactNode
  className?: string
  /** Chips pinned to the account header (same set collapsed and expanded). */
  pinnedChipIds?: readonly HeaderChipId[]
  onToggleHeaderChip?: (id: HeaderChipId) => void
  onResetHeaderChips?: () => void
  /** Rendered under the expand header when the panel is open (not inside the collapse control). */
  headerAccessory?: React.ReactNode
}

const keysFromBillingOptions = (options?: BillingOptionObject[]): string[] | undefined => {
  if (options == null || options.length === 0) return undefined
  return options.flatMap((obj) => Object.keys(obj))
}

const StatusChip: React.FC<{
  label: string
  chipKey: StatusChipKey
  possibleValues: string[]
  status?: string | null
  children?: React.ReactNode
  showTooltip?: boolean
  description?: string
}> = ({ label, chipKey, possibleValues, status, children, showTooltip = true, description }) => {
  const values =
    possibleValues.length > 0
      ? possibleValues
      : description != null
        ? []
        : [...STATUS_CHIP_POSSIBLE_VALUES[chipKey]]
  const tooltipContent = (
    <StatusChipTooltipContent
      label={label}
      values={values}
      currentStatus={status}
      description={description}
    />
  )

  const content = (
    <>
      <div className="whitespace-nowrap text-[10px] font-normal leading-tight text-king-blue dark:text-slate-200 sm:text-xs">
        {label}
      </div>
      <div className="flex w-full min-w-0 justify-end">
        {children ??
          (status != null ? (
            <Badge status={status} variant="soft" className="max-w-full min-w-0" />
          ) : null)}
      </div>
    </>
  )

  if (!showTooltip) {
    return <div className="flex w-full min-w-0 shrink-0 flex-col gap-0.5 px-1 text-right">{content}</div>
  }

  return (
    <PortaledTooltip content={tooltipContent} className="flex w-full min-w-0 shrink-0 flex-col gap-0.5 px-1 text-right">
      {content}
    </PortaledTooltip>
  )
}

const IdentityRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex min-w-0 items-center text-sm font-normal text-slate-600 dark:text-slate-300">
    {value ? (
      <CopyableValue
        value={value}
        label={label}
        valueClassName="font-mono font-normal text-slate-700 dark:text-slate-200"
      />
    ) : (
      <span className="px-1 py-0.5 font-mono font-normal text-slate-700 dark:text-slate-200">—</span>
    )}
  </div>
)

/** Tailwind gap-x-2 — must match STATUS_GRID_CLASS for overflow measurement. */
const HEADER_CHIP_ROW_GAP_PX = 8

interface HeaderChipItem {
  id: HeaderChipId
  node: React.ReactNode
}

const BODY_ANIMATION_MS = 200
/** Account sticky headers share z-20; lift this shell so the chip picker isn't covered by later panels. */
const STICKY_HEADER_MENU_Z = 35

export const TenantAccountPanel: React.FC<TenantAccountPanelProps> = ({
  account,
  billingStatuses,
  accountStatuses,
  billingOptions,
  open: openControlled,
  defaultOpen = false,
  forceOpen = false,
  expandAllOpen,
  expandAllVersion = 0,
  onOpenChange,
  isHighlighted = false,
  stickyOffset,
  stickyZIndex = 20,
  autoScrollOnExpand = true,
  children,
  className,
  pinnedChipIds,
  onToggleHeaderChip,
  onResetHeaderChips,
  headerAccessory,
}) => {
  const isControlled = openControlled != null
  const [openUncontrolled, setOpenUncontrolled] = useState(defaultOpen || forceOpen)
  const open = isControlled ? openControlled : openUncontrolled
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const chipRowRef = useRef<HTMLDivElement>(null)
  const chipMeasureRowRef = useRef<HTMLDivElement>(null)
  const pendingExpandScrollRef = useRef(false)
  const [bodyMounted, setBodyMounted] = useState(open)
  const [bodyExpanded, setBodyExpanded] = useState(open)
  // overflow-visible must wait until the grid-rows expand finishes; applying it with
  // bodyExpanded lets the min-h-0 child paint at full height from frame 0.
  const [bodyOverflowVisible, setBodyOverflowVisible] = useState(open)
  const [headerChipsMenuOpen, setHeaderChipsMenuOpen] = useState(false)
  const [visibleChipCount, setVisibleChipCount] = useState<number | null>(null)
  const [tabStickyTop, setTabStickyTop] = useState(stickyOffset ?? 0)
  const [topNavHost, setTopNavHost] = useState<HTMLElement | null>(null)
  const [sideNavHost, setSideNavHost] = useState<HTMLElement | null>(null)

  const setOpen = (next: boolean): void => {
    if (!isControlled) setOpenUncontrolled(next)
    onOpenChange?.(next)
  }

  useEffect(() => {
    if (forceOpen) {
      pendingExpandScrollRef.current = autoScrollOnExpand
      setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen])

  useEffect(() => {
    if (expandAllVersion > 0 && expandAllOpen != null) setOpen(expandAllOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAllVersion, expandAllOpen])

  const cancelExpandScrollRef = useRef<(() => void) | null>(null)

  const scrollPanelIntoView = (): void => {
    // Sticky headers make Element.scrollIntoView unreliable — scroll the app column instead.
    // Settle against the full accounts list so sibling expanded panels don't shift us mid-scroll.
    cancelExpandScrollRef.current?.()
    const section = sectionRef.current
    const accountsRoot =
      section?.closest('[data-tenant-accounts]') instanceof HTMLElement
        ? (section.closest('[data-tenant-accounts]') as HTMLElement)
        : null
    cancelExpandScrollRef.current = scheduleScrollAfterLayoutSettle(
      section,
      stickyOffset ?? 0,
      accountsRoot ?? section,
      { settleMs: 250, maxWaitMs: 3000, correctAfterMs: BODY_ANIMATION_MS + 200 }
    )
  }

  useEffect(() => {
    return () => {
      cancelExpandScrollRef.current?.()
      cancelExpandScrollRef.current = null
    }
  }, [])

  useEffect(() => {
    if (open) {
      setBodyMounted(true)
      const frame = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setBodyExpanded(true)
          if (pendingExpandScrollRef.current) {
            pendingExpandScrollRef.current = false
            scrollPanelIntoView()
          }
        })
      })
      return () => window.cancelAnimationFrame(frame)
    }

    pendingExpandScrollRef.current = false
    cancelExpandScrollRef.current?.()
    cancelExpandScrollRef.current = null
    setBodyExpanded(false)
    setBodyOverflowVisible(false)
    const timeout = window.setTimeout(() => setBodyMounted(false), BODY_ANIMATION_MS)
    return () => window.clearTimeout(timeout)
    // stickyOffset is read when scrolling; intentionally omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open || !bodyExpanded) return
    const timeout = window.setTimeout(() => setBodyOverflowVisible(true), BODY_ANIMATION_MS)
    return () => window.clearTimeout(timeout)
  }, [open, bodyExpanded])

  useLayoutEffect(() => {
    const headerEl = headerRef.current
    const bodyEl = bodyRef.current
    const sync = (): void => {
      const headerH = headerEl != null ? Math.max(0, Math.ceil(headerEl.getBoundingClientRect().height)) : 0
      // Body py-3 sits above the tab nav. Include it so sticky top matches the nav's in-flow
      // position and the rail does not slide up when pinning. +1px matches the sticky shell pt-px.
      const padTop =
        bodyEl != null ? Math.max(0, Math.round(Number.parseFloat(getComputedStyle(bodyEl).paddingTop) || 0)) : 0
      const shellPad = stickyOffset != null ? 1 : 0
      setTabStickyTop((stickyOffset ?? 0) + headerH + padTop + shellPad)
    }
    sync()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(sync)
    if (headerEl != null) observer.observe(headerEl)
    if (bodyEl != null) observer.observe(bodyEl)
    return () => observer.disconnect()
  }, [bodyMounted, bodyExpanded, pinnedChipIds, stickyOffset, visibleChipCount])

  const toggleOpen = (): void => {
    if (open) {
      setOpen(false)
      return
    }
    pendingExpandScrollRef.current = autoScrollOnExpand
    setOpen(true)
  }

  const onHeaderKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggleOpen()
    }
  }

  const possibleValues = useMemo(
    () => ({
      balance: [...STATUS_CHIP_POSSIBLE_VALUES.balance],
      balanceDue: [...STATUS_CHIP_POSSIBLE_VALUES.balanceDue],
      keAccount: [...STATUS_CHIP_POSSIBLE_VALUES.keAccount],
      utilityAccount: [...STATUS_CHIP_POSSIBLE_VALUES.utilityAccount],
      billingStanding: [...STATUS_CHIP_POSSIBLE_VALUES.billingStanding],
      billingOpsStatus:
        keysFromBillingOptions(billingOptions?.billingStatusOptions) ??
        [...STATUS_CHIP_POSSIBLE_VALUES.billingOpsStatus],
      billingMode: keysFromBillingOptions(billingOptions?.billingMode) ?? [...STATUS_CHIP_POSSIBLE_VALUES.billingMode],
      vnemStatus: [...STATUS_CHIP_POSSIBLE_VALUES.vnemStatus],
      paperless: [...STATUS_CHIP_POSSIBLE_VALUES.paperless],
      autopay: [...STATUS_CHIP_POSSIBLE_VALUES.autopay],
      optiRate: [...STATUS_CHIP_POSSIBLE_VALUES.optiRate],
    }),
    [billingOptions]
  )

  const billingStatus = billingStatuses[account.id]
  const accountStatus = accountStatuses[account.id]
  const billingModeChip = getBillingModeChip(account)
  const keAccountStatus = getKeAccountStatus(accountStatus?.status, account)
  const keAccountDescription = getPendingCloseTooltip(account, accountStatus)
  const utilityAccountStatus = getUtilityAccountStatus(account)
  const billingStandingStatus = getArBillingStanding(account)
  const billingStandingLabel = getArBillingStandingBadgeLabel(
    billingStandingStatus,
    account.daysPastDue
  )
  const billingStandingDescription =
    (billingStandingStatus === 'PAST_DUE' || billingStandingStatus === 'PAYMENT_PLAN_PAST_DUE') &&
    account.daysPastDue != null &&
    account.daysPastDue > 0
      ? `${account.daysPastDue} day${account.daysPastDue === 1 ? '' : 's'} past due`
      : undefined
  const billingOpsStatus = billingStatus?.current?.status ?? account.billingOpsStatus?.status ?? null
  const billingOpsReasons = billingStatus?.current?.reasons ?? account.billingOpsStatus?.reasons ?? []
  const billingOpsDescription =
    billingOpsReasons.length > 0 ? billingOpsReasons.join(', ') : undefined
  const vnemStatus = getVnemStatus(billingStatus, account)
  const paperlessStatus = account.mailingPreference.paperless ? 'ENABLED' : 'DISABLED'
  const autopayStatus = account.paymentPreference.autopay.enabled ? 'ENABLED' : 'DISABLED'
  const optiRateStatus = getOptiRateStatus(account)
  const pinned = new Set(pinnedChipIds ?? DEFAULT_HEADER_CHIP_IDS)

  const { balanceTotal: balanceTotalAmount, keBalanceTotal, balanceDue: balanceDueAmount } =
    getHeaderBalanceAmounts(account)

  const amountTone = (amount: number): string =>
    amount > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'

  const headerTone = isHighlighted
    ? 'bg-orange-50 dark:bg-[#3d3428]'
    : 'bg-indigo-100 dark:bg-indigo-500/15'

  const borderTone = isHighlighted
    ? 'border-orange-300 dark:border-amber-600/70'
    : 'border-slate-200 dark:border-slate-700'

  const isSticky = stickyOffset != null

  /** Value-only nodes for overflow chips in HeaderChipsPicker. */
  /** Soft badges in fixed chip slots — must shrink so long labels ellipsize. */
  const headerBadgeClass = 'max-w-full min-w-0'

  const chipValueById = useMemo((): Partial<Record<HeaderChipId, React.ReactNode>> => {
    return {
      balanceTotal: (
        <div className={classnames('whitespace-nowrap tabular-nums text-xs font-medium', amountTone(balanceTotalAmount))}>
          {formatCurrency(balanceTotalAmount)}
        </div>
      ),
      keBalanceTotal: (
        <div className={classnames('whitespace-nowrap tabular-nums text-xs font-medium', amountTone(keBalanceTotal))}>
          {formatCurrency(keBalanceTotal)}
        </div>
      ),
      balanceDue: (
        <div className={classnames('whitespace-nowrap tabular-nums text-xs font-medium', amountTone(balanceDueAmount))}>
          {formatCurrency(balanceDueAmount)}
        </div>
      ),
      keAccount:
        keAccountStatus != null ? (
          <Badge status={keAccountStatus} variant="soft" className={headerBadgeClass} />
        ) : null,
      utilityAccount:
        utilityAccountStatus != null ? (
          <Badge status={utilityAccountStatus} variant="soft" className={headerBadgeClass} />
        ) : null,
      billingStanding: (
        <Badge
          status={billingStandingStatus}
          variant="soft"
          className={headerBadgeClass}
          displayLabel={billingStandingLabel}
        />
      ),
      billingOpsStatus:
        billingOpsStatus != null ? (
          <Badge status={billingOpsStatus} variant="soft" className={headerBadgeClass} />
        ) : null,
      vnemStatus:
        vnemStatus != null ? <Badge status={vnemStatus} variant="soft" className={headerBadgeClass} /> : null,
      billingMode: (
        <Badge
          status={billingModeChip.status}
          variant="soft"
          className={headerBadgeClass}
          displayLabel={billingModeChip.displayLabel}
        />
      ),
      paperless: <Badge status={paperlessStatus} variant="soft" className={headerBadgeClass} />,
      autopay: <Badge status={autopayStatus} variant="soft" className={headerBadgeClass} />,
      optiRate:
        optiRateStatus != null ? (
          <Badge status={optiRateStatus} variant="soft" className={headerBadgeClass} />
        ) : null,
    }
  }, [
    balanceTotalAmount,
    keBalanceTotal,
    balanceDueAmount,
    keAccountStatus,
    utilityAccountStatus,
    billingStandingStatus,
    billingStandingLabel,
    billingOpsStatus,
    vnemStatus,
    billingModeChip.status,
    billingModeChip.displayLabel,
    paperlessStatus,
    autopayStatus,
    optiRateStatus,
  ])

  const pinnedChipItems = useMemo((): HeaderChipItem[] => {
    const show = (id: HeaderChipId): boolean => pinned.has(id)
    const chips: HeaderChipItem[] = []
    if (show('balanceTotal')) {
      chips.push({
        id: 'balanceTotal',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.balanceTotal}
            chipKey="balance"
            possibleValues={[]}
            description={HEADER_BALANCE_CHIP_TOOLTIPS.balanceTotal}
          >
            {chipValueById.balanceTotal}
          </StatusChip>
        ),
      })
    }
    if (show('keBalanceTotal')) {
      chips.push({
        id: 'keBalanceTotal',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.keBalanceTotal}
            chipKey="balance"
            possibleValues={[]}
            description={HEADER_BALANCE_CHIP_TOOLTIPS.keBalanceTotal}
          >
            {chipValueById.keBalanceTotal}
          </StatusChip>
        ),
      })
    }
    if (show('balanceDue')) {
      chips.push({
        id: 'balanceDue',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.balanceDue}
            chipKey="balanceDue"
            possibleValues={[]}
            description={HEADER_BALANCE_CHIP_TOOLTIPS.balanceDue}
          >
            {chipValueById.balanceDue}
          </StatusChip>
        ),
      })
    }
    if (show('keAccount')) {
      chips.push({
        id: 'keAccount',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.keAccount}
            chipKey="keAccount"
            possibleValues={possibleValues.keAccount}
            status={keAccountStatus}
            description={keAccountDescription}
          />
        ),
      })
    }
    if (show('utilityAccount')) {
      chips.push({
        id: 'utilityAccount',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.utilityAccount}
            chipKey="utilityAccount"
            possibleValues={possibleValues.utilityAccount}
            status={utilityAccountStatus}
          />
        ),
      })
    }
    if (show('billingStanding')) {
      chips.push({
        id: 'billingStanding',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.billingStanding}
            chipKey="billingStanding"
            possibleValues={possibleValues.billingStanding}
            status={billingStandingStatus}
            description={billingStandingDescription}
          >
            {chipValueById.billingStanding}
          </StatusChip>
        ),
      })
    }
    if (show('billingOpsStatus')) {
      chips.push({
        id: 'billingOpsStatus',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.billingOpsStatus}
            chipKey="billingOpsStatus"
            possibleValues={possibleValues.billingOpsStatus}
            status={billingOpsStatus}
            description={billingOpsDescription}
          />
        ),
      })
    }
    if (show('vnemStatus')) {
      chips.push({
        id: 'vnemStatus',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.vnemStatus}
            chipKey="vnemStatus"
            possibleValues={possibleValues.vnemStatus}
            status={vnemStatus}
          />
        ),
      })
    }
    if (show('billingMode')) {
      chips.push({
        id: 'billingMode',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.billingMode}
            chipKey="billingMode"
            possibleValues={
              billingModeChip.status === 'FINAL'
                ? [...possibleValues.billingMode, 'FINAL']
                : possibleValues.billingMode
            }
            status={billingModeChip.status}
          >
            {chipValueById.billingMode}
          </StatusChip>
        ),
      })
    }
    if (show('paperless')) {
      chips.push({
        id: 'paperless',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.paperless}
            chipKey="paperless"
            possibleValues={possibleValues.paperless}
            status={paperlessStatus}
          />
        ),
      })
    }
    if (show('autopay')) {
      chips.push({
        id: 'autopay',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.autopay}
            chipKey="autopay"
            possibleValues={possibleValues.autopay}
            status={autopayStatus}
          />
        ),
      })
    }
    if (show('optiRate')) {
      chips.push({
        id: 'optiRate',
        node: (
          <StatusChip
            label={HEADER_CHIP_LABELS.optiRate}
            chipKey="optiRate"
            possibleValues={possibleValues.optiRate}
            status={optiRateStatus}
          />
        ),
      })
    }
    return chips
  }, [
    pinnedChipIds,
    chipValueById,
    possibleValues,
    keAccountStatus,
    keAccountDescription,
    utilityAccountStatus,
    billingStandingStatus,
    billingStandingDescription,
    billingOpsStatus,
    billingOpsDescription,
    vnemStatus,
    billingModeChip.status,
    paperlessStatus,
    autopayStatus,
    optiRateStatus,
  ])

  const measureHeaderChipOverflow = useCallback(() => {
    const row = chipRowRef.current
    const measureRow = chipMeasureRowRef.current
    if (row == null || measureRow == null) {
      setVisibleChipCount(pinnedChipItems.length)
      return
    }
    const styles = window.getComputedStyle(row)
    const padX =
      (Number.parseFloat(styles.paddingLeft) || 0) + (Number.parseFloat(styles.paddingRight) || 0)
    // Content box only — clientWidth includes padding that chips cannot use.
    const availableWidth = Math.max(0, row.clientWidth - padX)
    const chipEls = [...measureRow.querySelectorAll<HTMLElement>('[data-header-chip-measure]')]
    const chipWidths = chipEls.map((el) => el.offsetWidth)
    // Overflow lives in HeaderChipsPicker (outside this row) — do not reserve a More control.
    // leadingWidth = -gap cancels the helper's gap-before-first-item.
    setVisibleChipCount(
      countFittingTabItems(
        chipWidths,
        availableWidth,
        -HEADER_CHIP_ROW_GAP_PX,
        0,
        HEADER_CHIP_ROW_GAP_PX
      )
    )
  }, [pinnedChipItems.length])

  useLayoutEffect(() => {
    measureHeaderChipOverflow()
    const row = chipRowRef.current
    if (row == null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measureHeaderChipOverflow())
    observer.observe(row)
    return () => observer.disconnect()
  }, [measureHeaderChipOverflow, pinnedChipItems])

  const fittedCount = visibleChipCount ?? pinnedChipItems.length
  const overflowStart = Math.min(fittedCount, pinnedChipItems.length)
  const visibleChips = pinnedChipItems.slice(0, overflowStart)
  const overflowChips = pinnedChipItems.slice(overflowStart).map((item) => ({
    id: item.id,
    node: chipValueById[item.id] ?? null,
  }))

  return (
    <AccountTabNavSlotsContext.Provider value={{ topHost: topNavHost, sideHost: sideNavHost }}>
    <section
      ref={sectionRef}
      id={account.kingAccountNumber}
      style={{
        ...(stickyOffset != null ? { scrollMarginTop: stickyOffset } : {}),
        // Pin the tab nav at its in-flow Y (sticky header bottom + body padding) so it does not slide.
        ['--account-tab-sticky-top' as string]: `${tabStickyTop}px`,
      }}
      className={classnames('min-w-0 w-full bg-slate-100 pb-6 dark:bg-slate-900', className)}
    >
      {/* Square sticky shell matches page bg so body borders can't peek through rounded header corners */}
      <div
        style={{
          ...(isSticky ? { top: stickyOffset } : {}),
          zIndex: headerChipsMenuOpen ? STICKY_HEADER_MENU_Z : isSticky ? stickyZIndex : undefined,
        }}
        className={classnames(
          isSticky &&
            'sticky isolate z-20 bg-slate-100 pt-px shadow-[0_-1px_0_0_#f1f5f9] dark:bg-slate-900 dark:shadow-[0_-1px_0_0_#0f172a]',
          headerChipsMenuOpen && !isSticky && 'relative'
        )}
      >
        <div
          ref={headerRef}
          role="button"
          tabIndex={0}
          aria-expanded={open}
          aria-label={open ? `Collapse ${account.kingAccountName}` : `Expand ${account.kingAccountName}`}
          onClick={toggleOpen}
          onKeyDown={onHeaderKeyDown}
          className={classnames(
            // Always one horizontal band — chips that do not fit go into More (never wrap under the title).
            'flex cursor-pointer flex-row items-center justify-between gap-3 border px-4 py-2.5',
            headerTone,
            borderTone,
            bodyMounted ? (isSticky ? 'rounded-t-lg' : 'rounded-t-lg border-b-0') : 'rounded-lg',
            isSticky && 'shadow-sm',
            'hover:brightness-[0.98] dark:hover:brightness-110'
          )}
        >
        <div className="flex min-w-0 max-w-[18rem] shrink items-start gap-2">
          <span
            aria-hidden
            className={classnames(
              'mt-0.5 inline-block w-3 shrink-0 text-slate-500 transition-transform duration-200 dark:text-slate-300',
              open && 'rotate-90'
            )}
          >
            ▸
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex w-full items-center gap-2 text-left text-base text-slate-900 dark:text-slate-100">
              <span className="truncate font-semibold">{account.kingAccountName}</span>
            </div>

            <div className="mt-1 space-y-0.5">
              <IdentityRow label="Tenant account number" value={account.kingAccountNumber} />
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 items-center gap-1">
          <div className="relative min-w-0 flex-1 lg:flex-1">
            <div
              ref={chipRowRef}
              className={classnames(STATUS_GRID_CLASS, 'rounded-md px-2 py-1')}
            >
              {visibleChips.map((item) => (
                <div
                  key={item.id}
                  className={classnames(
                    'shrink-0',
                    HEADER_CHIP_SLOT_CLASS[item.id],
                    !isHeaderAmountChip(item.id) && 'overflow-hidden'
                  )}
                  data-header-chip-id={item.id}
                >
                  {item.node}
                </div>
              ))}
            </div>
            <div
              ref={chipMeasureRowRef}
              className="pointer-events-none invisible absolute left-0 top-0 flex h-0 overflow-hidden"
              aria-hidden
            >
              <div className="flex w-max flex-nowrap items-center justify-end gap-x-2 px-2 py-1">
                {pinnedChipItems.map((item) => (
                  <div
                    key={item.id}
                    className={classnames(
                      'shrink-0',
                      HEADER_CHIP_SLOT_CLASS[item.id],
                      !isHeaderAmountChip(item.id) && 'overflow-hidden'
                    )}
                    data-header-chip-measure
                  >
                    {item.node}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {onToggleHeaderChip != null && onResetHeaderChips != null ? (
            <HeaderChipsPicker
              pinnedChipIds={pinnedChipIds ?? DEFAULT_HEADER_CHIP_IDS}
              onToggle={onToggleHeaderChip}
              onReset={onResetHeaderChips}
              onOpenChange={setHeaderChipsMenuOpen}
              overflowChips={overflowChips}
              chipValues={chipValueById}
            />
          ) : null}
        </div>
        </div>
        {open && headerAccessory != null ? (
          <div className={classnames('border-x bg-white px-4 py-1.5 dark:bg-slate-800', borderTone)}>
            {headerAccessory}
          </div>
        ) : null}
        <div
          ref={setTopNavHost}
          className={classnames(
            'empty:hidden border-x bg-white px-4 pt-3 dark:bg-slate-800',
            borderTone
          )}
        />
      </div>

      {bodyMounted && (
        <div
          className={classnames(
            'grid min-w-0 transition-[grid-template-rows] duration-200 ease-out',
            bodyExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div
            className={classnames(
              'min-h-0 min-w-0',
              // Clip through the grid-rows animation. After it ends, overflow-visible lets
              // the side tab rail stick. Tab *content* stays overflow-hidden so sticky
              // table headers cannot attach to the page (and leak after this panel scrolls away).
              bodyExpanded && 'relative z-0',
              bodyOverflowVisible ? 'overflow-visible' : 'overflow-hidden'
            )}
          >
            <div
              ref={bodyRef}
              className={classnames(
                'relative z-0 flex min-w-0 items-start gap-3 rounded-b-lg border-x border-b bg-white px-4 py-3 dark:bg-slate-800 lg:gap-6',
                borderTone
              )}
            >
              <div ref={setSideNavHost} className="contents" />
              <div className="min-w-0 flex-1 overflow-hidden">{children}</div>
            </div>
          </div>
        </div>
      )}
    </section>
    </AccountTabNavSlotsContext.Provider>
  )
}

export default TenantAccountPanel
